import { LogOut, RefreshCcw } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { VIEW_MODE } from "../constants";
import { useCommentAttention } from "../hooks/useCommentAttention";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useMergeRequests } from "../hooks/useMergeRequests";
import { useRecentPushes } from "../hooks/useRecentPushes";
import { useReviewerThreads } from "../hooks/useReviewerThreads";
import { asTicketKey, getTicketKeys } from "../jiraTickets";
import { applyQuickFilters } from "../quickFilters";
import { Avatar } from "./Avatar";
import { AvatarPreloader } from "./AvatarPreloader";
import { FilterButton } from "./FilterButton";
import { FreshnessIndicator } from "./FreshnessIndicator";
import { TicketFilterContext } from "./JiraBadge";
import { QuickFilters } from "./QuickFilters";
import { ReviewBoard } from "./ReviewBoard";
import { SearchBar } from "./SearchBar";
import { ThemeMenu } from "./ThemeMenu";
import { ViewMenu } from "./ViewMenu";
import { CommentAttentionView } from "./dashboardViews/commentAttention";
import { JiraView } from "./dashboardViews/jira";
import { RepoView } from "./dashboardViews/repo";
import { StandardView } from "./dashboardViews/standard";
import { SwimlaneView } from "./dashboardViews/swimlane";

export const Dashboard = () => {
	const { user, logout } = useAuth();
	const [showDrafts, setShowDrafts] = useLocalStorage("showDrafts", true);
	const [showBotPRs, setShowBotPRs] = useLocalStorage("showBotPRs", true);
	const [sortOption, setSortOption] = useLocalStorage(
		"sortOption",
		"updatedAt",
	);
	const [showPendingWithProjectRules, setShowPendingWithProjectRules] =
		useLocalStorage("showPendingWithProjectRules", true);
	const [showPendingWithAllApprovals, setShowPendingWithAllApprovals] =
		useLocalStorage("showPendingWithAllApprovals", true);
	const [watchProjects, setWatchProjects] = useLocalStorage(
		"watchProjects",
		[],
	);
	const [excludeProjects, setExcludeProjects] = useLocalStorage(
		"excludeProjects",
		[],
	);
	const [greyOutBlockingComments, setGreyOutBlockingComments] = useLocalStorage(
		"greyOutBlockingComments",
		true,
	);
	const [enableCommentsExpansion, setEnableCommentsExpansion] = useLocalStorage(
		"enableCommentsExpansion",
		false,
	);
	// Quick filter presentation: "icons" (compact round icon-only chips, the
	// default) or "pills" (labelled pill chips in an elevated card).
	const [quickFilterStyle, setQuickFilterStyle] = useLocalStorage(
		"quickFilterStyle",
		"icons",
	);
	// View mode; see VIEW_MODE in src/constants.js for the full set (standard,
	// swimlane, reviewboard, repo, jira, comment-attention).
	const [viewMode, setViewMode] = useLocalStorage(
		"viewMode",
		VIEW_MODE.STANDARD,
	);

	// Migration: convert old useSwimlaneView boolean to new viewMode string
	useEffect(() => {
		const oldValue = localStorage.getItem("useSwimlaneView");
		if (oldValue !== null) {
			const wasEnabled = JSON.parse(oldValue);
			if (wasEnabled && viewMode === VIEW_MODE.STANDARD) {
				setViewMode(VIEW_MODE.SWIMLANE);
			}
			localStorage.removeItem("useSwimlaneView");
		}
	}, [setViewMode, viewMode]);

	const [showSwimlaneMergedColumn, setShowSwimlaneMergedColumn] =
		useLocalStorage("showSwimlaneMergedColumn", true);
	const [showSwimlaneDeletedColumn, setShowSwimlaneDeletedColumn] =
		useLocalStorage("showSwimlaneDeletedColumn", false);
	const [swimlaneWorkstreamsEnabled, setSwimlaneWorkstreamsEnabled] =
		useLocalStorage("swimlaneWorkstreamsEnabled", true);
	const [useLocalTime, setUseLocalTime] = useLocalStorage(
		"useLocalTime",
		false,
	);
	const [
		swimlaneHideCreatedDaysThreshold,
		setSwimlaneHideCreatedDaysThreshold,
	] = useLocalStorage("swimlaneHideCreatedDaysThreshold", 30);
	const [
		swimlaneHideUpdatedDaysThreshold,
		setSwimlaneHideUpdatedDaysThreshold,
	] = useLocalStorage("swimlaneHideUpdatedDaysThreshold", 14);
	const [searchQuery, setSearchQuery] = useState("");
	const [quickFilters, setQuickFilters] = useLocalStorage("quickFilters", {});

	const toggleQuickFilter = (id) =>
		setQuickFilters((prev) => ({ ...prev, [id]: !prev[id] }));
	const clearQuickFilters = () => setQuickFilters({});

	// The toolbar and quick-filter bar span the widest content column so the
	// chrome is consistent across every view.
	const chromeWidthClass = "max-w-7xl";

	const {
		pendingReviewMRs: sourcePendingReviewMRs,
		myOpenMRs,
		recentlyClosedMRs,
		loading,
		loadingClosed,
		error,
		refetch,
		userAvatar,
		isRefreshing,
		hasIncompleteResults,
		lastUpdated,
	} = useMergeRequests(watchProjects);

	// Annotate before filtering so every view (and its sort/grey-out logic) sees
	// which unresolved threads the viewer opened and whether they were answered.
	const reviewerThreads = useReviewerThreads(
		sourcePendingReviewMRs,
		lastUpdated,
	);
	const pendingReviewMRs = useMemo(
		() =>
			reviewerThreads.summaries.size === 0
				? sourcePendingReviewMRs
				: sourcePendingReviewMRs.map((mr) =>
						reviewerThreads.summaries.has(mr.id)
							? { ...mr, reviewerThreads: reviewerThreads.summaries.get(mr.id) }
							: mr,
					),
		[sourcePendingReviewMRs, reviewerThreads.summaries],
	);

	const { pushesWithoutMR } = useRecentPushes();

	const filterMRs = (mrs) => {
		const excludeProjectPaths = excludeProjects.map((p) => p.id);
		const ticketKey = asTicketKey(searchQuery);

		const base = mrs.filter((mr) => {
			if (!showDrafts && mr.draft) return false;
			if (!showBotPRs && (mr.author?.bot ?? false)) return false;
			if (ticketKey) {
				if (!getTicketKeys(mr).includes(ticketKey)) return false;
			} else if (searchQuery) {
				const query = searchQuery.toLowerCase();
				const fields = [
					mr.title,
					mr.sourceBranch,
					mr.author?.name,
					mr.author?.username,
					mr.projectPath,
				];
				if (!fields.some((field) => field?.toLowerCase().includes(query)))
					return false;
			}
			// Check both project.fullPath and projectPath for exclusion
			const projectPath = mr.project?.fullPath || mr.projectPath;
			if (excludeProjectPaths.includes(projectPath)) return false;
			return true;
		});

		return applyQuickFilters(base, quickFilters);
	};

	const filteredPendingReviewMRs = filterMRs(pendingReviewMRs);
	const filteredMyOpenMRs = filterMRs(myOpenMRs);
	const filteredRecentlyClosedMRs = filterMRs(recentlyClosedMRs);
	// Key the discussion fetch on the unfiltered MR set so search keystrokes and
	// filter-chip toggles do not re-trigger the whole GraphQL pipeline; apply the
	// same client-side filters to the classified results instead.
	// Opt-in: start once the MR list has loaded, in the background on other
	// views, so the inbox opens warm and the View menu can show its count.
	// Off, the inbox loads only while open (the count then reflects that load).
	const [preloadResponses, setPreloadResponses] = useLocalStorage(
		"preloadResponses",
		false,
	);
	const onCommentAttentionView = viewMode === VIEW_MODE.COMMENT_ATTENTION;
	const commentAttention = useCommentAttention(
		myOpenMRs,
		!loading && (preloadResponses || onCommentAttentionView),
		{ background: !onCommentAttentionView, pollKey: lastUpdated },
	);
	const filteredCommentAttentionItems = filterMRs(commentAttention.items);
	const pendingResponseCount = filteredCommentAttentionItems.reduce(
		(total, mr) => total + mr.commentAttention.count,
		0,
	);
	const refreshData = async () => {
		commentAttention.refetch();
		reviewerThreads.refetch();
		await refetch();
	};

	const dashboard = (
		<div className="min-h-screen bg-[var(--color-bg-app)]">
			<AvatarPreloader
				pendingReviewMRs={pendingReviewMRs}
				myOpenMRs={myOpenMRs}
				recentlyClosedMRs={recentlyClosedMRs}
			/>
			<div className={`${chromeWidthClass} mx-auto px-4 pt-2.5 pb-1.5`}>
				<div className="flex items-center gap-2 rounded-lg bg-[var(--surface-elevated)] px-2 py-1 shadow-sm ring-1 ring-[var(--surface-elevated-ring)]">
					<div className="flex shrink-0 items-center gap-2 pl-0.5">
						<Avatar src={userAvatar} alt={user?.profile?.name} size="sm" />
						<span className="hidden text-xs font-medium text-[var(--color-text-primary)] sm:inline">
							{user?.profile?.name}
						</span>
					</div>
					<div className="h-5 w-px shrink-0 bg-[var(--color-border)]" />
					<div className="flex min-w-0 flex-1 items-center">
						<SearchBar
							searchQuery={searchQuery}
							onSearchChange={setSearchQuery}
						/>
					</div>
					<div className="flex shrink-0 items-center gap-1">
						<ViewMenu
							viewMode={viewMode}
							onSetViewMode={setViewMode}
							counts={{ [VIEW_MODE.COMMENT_ATTENTION]: pendingResponseCount }}
						/>
					</div>
					<div className="h-5 w-px shrink-0 bg-[var(--color-border)]" />
					<div className="flex shrink-0 items-center gap-0.5">
						<FreshnessIndicator lastUpdated={lastUpdated} />
						<button
							type="button"
							onClick={refreshData}
							disabled={isRefreshing}
							aria-label={isRefreshing ? "Refreshing" : "Refresh"}
							aria-busy={isRefreshing}
							title="Refresh"
							className="group flex h-8 w-8 items-center justify-center rounded-md text-[var(--surface-elevated-muted)] hover:bg-[var(--surface-elevated-hover)] hover:text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-opacity-50 active:scale-[0.96] transition-[background-color,color,transform] disabled:cursor-not-allowed disabled:opacity-60"
						>
							<RefreshCcw
								strokeWidth={2}
								className={`${isRefreshing ? "animate-spin " : ""}w-3.5 h-3.5 transform rotate-180 transition-transform duration-500 ease-out motion-safe:group-hover:-rotate-[160deg]`}
							/>
						</button>
						<ThemeMenu />
						<FilterButton
							showDrafts={showDrafts}
							showBotPRs={showBotPRs}
							onToggleDrafts={setShowDrafts}
							onToggleBotPRs={setShowBotPRs}
							sortOption={sortOption}
							onSetSortOption={setSortOption}
							showPendingWithProjectRules={showPendingWithProjectRules}
							onTogglePendingWithProjectRules={setShowPendingWithProjectRules}
							showPendingWithAllApprovals={showPendingWithAllApprovals}
							onTogglePendingWithAllApprovals={setShowPendingWithAllApprovals}
							greyOutBlockingComments={greyOutBlockingComments}
							onToggleGreyOutBlockingComments={setGreyOutBlockingComments}
							enableCommentsExpansion={enableCommentsExpansion}
							onToggleCommentsExpansion={setEnableCommentsExpansion}
							preloadResponses={preloadResponses}
							onTogglePreloadResponses={setPreloadResponses}
							quickFilterStyle={quickFilterStyle}
							onSetQuickFilterStyle={setQuickFilterStyle}
							viewMode={viewMode}
							onSetViewMode={setViewMode}
							showSwimlaneMergedColumn={showSwimlaneMergedColumn}
							onToggleShowSwimlaneMergedColumn={setShowSwimlaneMergedColumn}
							showSwimlaneDeletedColumn={showSwimlaneDeletedColumn}
							onToggleShowSwimlaneDeletedColumn={setShowSwimlaneDeletedColumn}
							swimlaneWorkstreamsEnabled={swimlaneWorkstreamsEnabled}
							onToggleSwimlaneWorkstreams={setSwimlaneWorkstreamsEnabled}
							useLocalTime={useLocalTime}
							onToggleUseLocalTime={setUseLocalTime}
							watchProjects={watchProjects}
							setWatchProjects={setWatchProjects}
							excludeProjects={excludeProjects}
							setExcludeProjects={setExcludeProjects}
							swimlaneHideCreatedDaysThreshold={
								swimlaneHideCreatedDaysThreshold
							}
							setSwimlaneHideCreatedDaysThreshold={
								setSwimlaneHideCreatedDaysThreshold
							}
							swimlaneHideUpdatedDaysThreshold={
								swimlaneHideUpdatedDaysThreshold
							}
							setSwimlaneHideUpdatedDaysThreshold={
								setSwimlaneHideUpdatedDaysThreshold
							}
						/>
					</div>
					<div className="h-5 w-px shrink-0 bg-[var(--color-border)]" />
					<button
						type="button"
						onClick={logout}
						aria-label="Logout"
						title="Logout"
						className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[var(--surface-elevated-muted)] hover:bg-red-500/10 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/50 active:scale-[0.96] transition-[background-color,color,transform]"
					>
						<LogOut className="h-3.5 w-3.5 transition-transform duration-300 ease-out motion-safe:group-hover:translate-x-0.5" />
					</button>
				</div>
			</div>

			<div className={`${chromeWidthClass} mx-auto px-4 py-2`}>
				<QuickFilters
					filters={quickFilters}
					onToggle={toggleQuickFilter}
					onClear={clearQuickFilters}
					variant={quickFilterStyle}
				/>
			</div>

			{viewMode === VIEW_MODE.COMMENT_ATTENTION ? (
				<div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
					<CommentAttentionView
						items={filteredCommentAttentionItems}
						loading={loading || commentAttention.loading}
						pending={commentAttention.pending}
						unchecked={commentAttention.unchecked}
						onRetry={commentAttention.refetch}
						error={error || commentAttention.error}
					/>
				</div>
			) : viewMode === VIEW_MODE.SWIMLANE ? (
				<div className="px-4">
					<SwimlaneView
						pendingReviewMRs={filteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						recentlyClosedMRs={filteredRecentlyClosedMRs}
						sourcePendingReviewMRs={pendingReviewMRs}
						sourceMyOpenMRs={myOpenMRs}
						sourceRecentlyClosedMRs={recentlyClosedMRs}
						loading={loading}
						error={error}
						showMergedColumn={showSwimlaneMergedColumn}
						showDeletedColumn={showSwimlaneDeletedColumn}
						workstreamsEnabled={swimlaneWorkstreamsEnabled}
						hasIncompleteResults={hasIncompleteResults}
						hideCreatedDaysThreshold={swimlaneHideCreatedDaysThreshold}
						hideUpdatedDaysThreshold={swimlaneHideUpdatedDaysThreshold}
						enableCommentsExpansion={enableCommentsExpansion}
					/>
				</div>
			) : viewMode === VIEW_MODE.REVIEW_BOARD ? (
				<div className="px-4">
					<ReviewBoard
						pendingReviewMRs={filteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						recentlyClosedMRs={filteredRecentlyClosedMRs}
						loading={loading}
						error={error}
						sortOption={sortOption}
						showReadyToMergeColumn={showPendingWithAllApprovals}
						showApprovedPendingColumn={showPendingWithProjectRules}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
					/>
				</div>
			) : viewMode === VIEW_MODE.REPO ? (
				<div className="px-4">
					<RepoView
						pendingReviewMRs={filteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						loading={loading}
						error={error}
						sortOption={sortOption}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
					/>
				</div>
			) : viewMode === VIEW_MODE.JIRA ? (
				<div className="px-4">
					<JiraView
						pendingReviewMRs={filteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						loading={loading}
						error={error}
						sortOption={sortOption}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
					/>
				</div>
			) : (
				<div className="max-w-4xl mx-auto px-4">
					<StandardView
						pendingReviewMRs={filteredPendingReviewMRs}
						myOpenMRs={filteredMyOpenMRs}
						recentlyClosedMRs={filteredRecentlyClosedMRs}
						loading={loading}
						loadingClosed={loadingClosed}
						error={error}
						refetch={refetch}
						isRefreshing={isRefreshing}
						pushesWithoutMR={pushesWithoutMR}
						sortOption={sortOption}
						showPendingWithProjectRules={showPendingWithProjectRules}
						showPendingWithAllApprovals={showPendingWithAllApprovals}
						greyOutBlockingComments={greyOutBlockingComments}
						enableCommentsExpansion={enableCommentsExpansion}
					/>
				</div>
			)}
		</div>
	);

	// Lets a ticket badge on any card filter the dashboard via the search box.
	return (
		<TicketFilterContext.Provider value={setSearchQuery}>
			{dashboard}
		</TicketFilterContext.Provider>
	);
};
